/**
 * Kennzeichnet im sessionStorage, dass die Anwendung in diesem Browser-Kontext bereits gestartet wurde.
 * Der sessionStorage überlebt einen Reload, nicht aber das Beenden oder den Absturz der App.
 */
const SESSION_STARTED_KEY = 'app.sessionStarted';

/**
 * Ergebnis der Erkennung. Wird gecacht, weil die Erkennung nur beim ersten Aufruf
 * möglich ist (danach ist die Kennzeichnung im sessionStorage gesetzt).
 */
let reloadDetected: boolean | undefined;

/**
 * Wurde die Seite neu geladen, statt die Anwendung neu zu starten?
 *
 * Ein Reload entsteht durch einen gewollten `window.location.reload()`, z.B. beim Sprachwechsel,
 * beim Service-Worker-Update oder beim Ändern von Funktionsschaltern. Ein Neustart entsteht,
 * wenn die App beendet wurde oder abgestürzt ist.
 *
 * Erkannt wird über zwei unabhängige Wege:
 * - Die Navigation Timing API meldet bei `window.location.reload()` den Typ `reload`.
 * - Der sessionStorage überlebt einen Reload, aber keinen Neustart der App.
 *
 * @returns {boolean} true, wenn die Seite neu geladen wurde
 */
export function wasPageReloaded(): boolean {
  if (reloadDetected !== undefined) return reloadDetected;

  reloadDetected = isReloadNavigation() || sessionWasAlreadyStarted();

  try {
    sessionStorage.setItem(SESSION_STARTED_KEY, '1');
  } catch (error) {
    // Der sessionStorage ist nicht zwingend verfügbar, die Navigation Timing API greift dann allein
    console.error('Could not mark the session as started.');
    console.error(error);
  }

  return reloadDetected;
}

/**
 * Meldet die Navigation Timing API einen Reload?
 * @returns {boolean}
 */
function isReloadNavigation(): boolean {
  const entries = performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];

  return entries.length > 0 && entries[0].type === 'reload';
}

/**
 * War die Anwendung in diesem Browser-Kontext schon einmal gestartet?
 * @returns {boolean}
 */
function sessionWasAlreadyStarted(): boolean {
  try {
    return sessionStorage.getItem(SESSION_STARTED_KEY) === '1';
  } catch (error) {
    return false;
  }
}

/**
 * Setzt die Erkennung zurück. Nur für Tests.
 */
export function resetReloadDetectionForTests(): void {
  reloadDetected = undefined;
}
